import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Search, Download, Filter, X, Database, Server, RefreshCw, RotateCw } from 'lucide-react';
import EnhancedTableBase from '../../../components/common/EnhancedTable/EnhancedTableBase';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import '../../../components/common/EnhancedTable/EnhancedTableBase.css';
import './EnhancedAuditLogs.css';
import { getAuditLogs, searchAuditLogs, createAuditLog, getAllLogs, AuditLog, getAuditLogsDirectly } from '../../../services/supabase/audit-logs';
import { downloadCsv, formatTimestamp } from '../../../utils/exportUtils';
import { supabase } from '../../../lib/supabase';

export default function EnhancedAuditLogs() {
  const queryClient = useQueryClient();
  const [remountKey, setRemountKey] = useState(Date.now()); // Force remount key
  const directFetchRef = useRef(false); // Flag to bypass cache
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    action: null as string | null,
    user: null as string | null,
    source: null as string | null,
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'timestamp',
    direction: 'desc' as 'asc' | 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [directFetchedLogs, setDirectFetchedLogs] = useState<AuditLog[]>([]);
  const [totalLogCount, setTotalLogCount] = useState<number>(0);
  
  // Direct database query function that completely bypasses React Query
  const fetchLogsDirectly = async () => {
    const timestamp = Date.now();
    console.log(`Direct database fetch at: ${timestamp}`);
    
    try {
      // Use a completely fresh approach with raw SQL
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .order('timestamp', { ascending: false });
      
      if (error) {
        console.error('Error in direct fetch:', error);
        return [];
      }
      
      console.log(`Direct fetch returned ${data?.length || 0} logs at ${timestamp}`);
      
      // Check for test user logs
      const testUserEmail = 'mattssubscriptions@gmail.com';
      const testUserLogs = data?.filter((log: AuditLog) => 
        log.user_name && log.user_name.toLowerCase() === testUserEmail.toLowerCase()
      );
      
      console.log(`Test user logs in direct fetch: ${testUserLogs?.length || 0}`);
      
      // Force a fresh result by cloning the data
      return JSON.parse(JSON.stringify(data || []));
    } catch (err) {
      console.error('Unexpected error in direct fetch:', err);
      return [];
    }
  };
  
  // Fetch audit logs data with improved React Query configuration
  const { data: logs = [], isLoading, error, refetch } = useQuery({
    queryKey: ['audit-logs', searchQuery, filters.source, remountKey],
    queryFn: async () => {
      console.log('Fetching audit logs with query:', searchQuery, 'and source filter:', filters.source);
      
      // Always use direct fetch to bypass all caching
      try {
        const directLogs = await fetchLogsDirectly();
        console.log(`Query function received ${directLogs.length} logs`);
        
        // Store in state for backup
        setDirectFetchedLogs(directLogs);
        setTotalLogCount(directLogs.length);
        
        // If search query is provided, filter the direct logs
        if (searchQuery) {
          const filteredLogs = directLogs.filter((log: AuditLog) => 
            log.action?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            log.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            log.details?.toLowerCase().includes(searchQuery.toLowerCase())
          );
          console.log(`Search filtered logs: ${filteredLogs.length}`);
          return filteredLogs;
        }
        
        // Filter by source if needed
        if (filters.source) {
          const sourceLogs = directLogs.filter((log: AuditLog) => log.source === filters.source);
          console.log(`Source filtered logs: ${sourceLogs.length}`);
          return sourceLogs;
        }
        
        return directLogs;
      } catch (err) {
        console.error('Direct fetch failed in query function:', err);
        
        // Fall back to the original methods as a last resort
        if (searchQuery) {
          return searchAuditLogs(searchQuery);
        }
        
        return getAllLogs();
      }
    },
    // React Query configuration
    staleTime: 0, // Always consider data stale
    refetchOnWindowFocus: true,
    refetchInterval: 300 * 1000, // 5 minutes
    refetchIntervalInBackground: false,
    retry: 3,
    gcTime: 0, // No garbage collection time
    refetchOnMount: 'always'
  });
  
  // Use the logs from React Query or direct fetch as a fallback
  const effectiveLogs = useMemo(() => {
    return logs && logs.length > 0 ? logs : directFetchedLogs;
  }, [logs, directFetchedLogs]);
  
  // Handle manual refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      console.log('Manual refresh triggered');
      
      // Directly fetch logs bypassing React Query entirely
      const freshLogs = await fetchLogsDirectly();
      setDirectFetchedLogs(freshLogs);
      setTotalLogCount(freshLogs.length);
      
      // Force component remount
      setRemountKey(Date.now());
      
      // Invalidate all queries
      await queryClient.invalidateQueries();
      
      // Refetch data
      await refetch();
    } catch (err) {
      console.error('Error refreshing audit logs:', err);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Handle hard reload
  const handleHardReload = () => {
    console.log('Hard page reload triggered');
    window.location.reload();
  };
  
  // Auto-refresh on mount and when the component gains focus
  useEffect(() => {
    // Refresh on mount
    handleRefresh();
    
    // Set up event listener for when the page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible, refreshing audit logs');
        handleRefresh();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Set up interval for periodic refresh
    const refreshInterval = setInterval(() => {
      console.log('Interval refresh triggered');
      handleRefresh();
    }, 300000); // 5 minutes
    
    // Clean up
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, []);

  // Extract unique actions and users for filter dropdowns
  const { actions, users } = useMemo(() => {
    const actionsSet = new Set<string>();
    const usersSet = new Set<string>();

    effectiveLogs.forEach((log: AuditLog) => {
      if (log.action) actionsSet.add(log.action);
      if (log.user_name) usersSet.add(log.user_name);
    });

    return {
      actions: Array.from(actionsSet).sort(),
      users: Array.from(usersSet).sort()
    };
  }, [effectiveLogs]);

  // Apply filters
  const filteredLogs = useMemo(() => {
    if (!effectiveLogs || effectiveLogs.length === 0) return [];

    console.log(`Total logs before filtering: ${effectiveLogs.length}`);

    return effectiveLogs.filter((log: AuditLog) => {
      // Action filter
      if (filters.action && log.action !== filters.action) {
        return false;
      }

      // User filter
      if (filters.user && log.user_name !== filters.user) {
        return false;
      }
      
      // Source filter
      if (filters.source && log.source !== filters.source) {
        return false;
      }

      return true;
    });
  }, [effectiveLogs, filters]);

  // Apply sorting
  const sortedLogs = useMemo(() => {
    if (!filteredLogs || filteredLogs.length === 0) return [];
    
    console.log(`Filtered logs count: ${filteredLogs.length}`);
    
    return [...filteredLogs].sort((a: AuditLog, b: AuditLog) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'timestamp':
          return direction * (new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        case 'action':
          return direction * a.action.localeCompare(b.action);
        case 'user_name':
          return direction * a.user_name.localeCompare(b.user_name);
        default:
          return 0;
      }
    });
  }, [filteredLogs, sortConfig]);

  // Apply pagination
  const paginatedLogs = useMemo(() => {
    if (!sortedLogs || sortedLogs.length === 0) {
      console.log('No logs to paginate');
      return [];
    }
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const result = sortedLogs.slice(startIndex, endIndex);
    
    console.log(`Paginated logs count: ${result.length} (page ${currentPage}, size ${pageSize})`);
    
    // Log the first few entries for debugging
    if (result.length > 0) {
      console.log('First few log entries:', result.slice(0, 3).map((log: AuditLog) => ({
        timestamp: log.timestamp,
        action: log.action,
        user: log.user_name
      })));
    }
    
    return result;
  }, [sortedLogs, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    setSortConfig(sortConfig);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle remove filter
  const handleRemoveFilter = (key: keyof typeof filters) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle export to CSV
  const handleExport = () => {
    if (!effectiveLogs?.length) return;
    
    const headers = ['Timestamp', 'Action', 'User', 'Details'].join(',');
    const rows = effectiveLogs.map((log: AuditLog) => [
      new Date(log.timestamp).toLocaleString(),
      log.action,
      log.user_name,
      log.details
    ].map(value => `"${value || ''}"`).join(','));

    downloadCsv([headers, ...rows].join('\n'), `audit-logs-${formatTimestamp(new Date())}.csv`);
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedLogs.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Define table columns
  const columns: TableColumn<AuditLog>[] = [
    {
      id: 'timestamp',
      header: 'Timestamp',
      accessor: (row: AuditLog) => (
        <div className="whitespace-nowrap">
          {new Date(row.timestamp).toLocaleString()}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    {
      id: 'action',
      header: 'Action',
      accessor: (row: AuditLog) => (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
          {row.action}
        </span>
      ),
      width: 'narrow' as ColumnWidth,
      sortable: true
    },
    {
      id: 'user_name',
      header: 'User',
      accessor: (row: AuditLog) => row.user_name,
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    {
      id: 'details',
      header: 'Details',
      accessor: (row: AuditLog) => (
        <div className="truncate max-w-md" title={row.details}>
          {row.details}
        </div>
      ),
      width: 'wide' as ColumnWidth
    },
    {
      id: 'source',
      header: 'Source',
      accessor: (row: AuditLog) => {
        if (!row.source || row.source === 'custom') {
          return (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
              <Database className="h-3 w-3 mr-1" />
              App
            </span>
          );
        } else {
          return (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
              <Server className="h-3 w-3 mr-1" />
              System
            </span>
          );
        }
      },
      width: 'narrow' as ColumnWidth
    }
  ];

  if (isLoading && !isRefreshing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading audit logs. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-audit-logs-container p-4" key={remountKey}>
      <div className="enhanced-audit-logs-header">
        <div className="space-y-4">
          <div className="flex items-center mb-4">
            <FileText className="h-6 w-6 text-gray-400 mr-2" />
            <h1 className="text-2xl font-bold">Audit Logs</h1>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search audit logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Refresh button */}
            <button
              className={`p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1 ${isRefreshing ? 'opacity-50' : ''}`}
              onClick={handleRefresh}
              disabled={isRefreshing}
              title="Refresh audit logs"
            >
              <RefreshCw className={`h-5 w-5 text-gray-500 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            
            {/* Hard reload button */}
            <button
              className="p-2 border border-red-300 rounded-md hover:bg-red-50 flex items-center gap-1 text-red-600"
              onClick={handleHardReload}
              title="Hard reload page"
            >
              <RotateCw className="h-5 w-5" />
              <span className="hidden sm:inline">Hard Reload</span>
            </button>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            
            {/* Export button */}
            <button 
              onClick={handleExport}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={!effectiveLogs?.length}
            >
              <Download className="h-5 w-5 mr-2" />
              Export
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Logs: {sortedLogs.length} (DB: {totalLogCount})
            </div>
          </div>

          {/* Active filters */}
          {Object.values(filters).some(Boolean) && (
            <div className="active-filters">
              {filters.action && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Action:</span>
                  <span>{filters.action}</span>
                  <button
                    onClick={() => handleRemoveFilter('action')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.user && (
                <div className="filter-tag">
                  <span className="filter-tag-label">User:</span>
                  <span>{filters.user}</span>
                  <button
                    onClick={() => handleRemoveFilter('user')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.source && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Source:</span>
                  <span>{filters.source === 'custom' ? 'Application logs' : 'System logs'}</span>
                  <button
                    onClick={() => handleRemoveFilter('source')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    action: null,
                    user: null,
                    source: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Action filter */}
                <div className="filter-group">
                  <label htmlFor="action-filter">Action</label>
                  <select
                    id="action-filter"
                    value={filters.action || ''}
                    onChange={(e) => handleFilterChange({ action: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All actions</option>
                    {actions.map(action => (
                      <option key={action} value={action}>{action}</option>
                    ))}
                  </select>
                </div>
                
                {/* User filter */}
                <div className="filter-group">
                  <label htmlFor="user-filter">User</label>
                  <select
                    id="user-filter"
                    value={filters.user || ''}
                    onChange={(e) => handleFilterChange({ user: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All users</option>
                    {users.map(user => (
                      <option key={user} value={user}>{user}</option>
                    ))}
                  </select>
                </div>
                
                {/* Source filter */}
                <div className="filter-group">
                  <label htmlFor="source-filter">Log Source</label>
                  <select
                    id="source-filter"
                    value={filters.source || ''}
                    onChange={(e) => handleFilterChange({ source: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All sources</option>
                    <option value="custom">Application logs</option>
                    <option value="system">System logs</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="enhanced-audit-logs-table-container enhanced-table-force-scroll">
        <EnhancedTableBase
          columns={columns}
          data={paginatedLogs}
          keyField="id"
          width="1200px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading || isRefreshing}
          emptyMessage="No audit logs found"
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
        />
      </div>
    </div>
  );
}
